import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from './api';

export type Level = 'none' | 'view' | 'create_own' | 'edit' | 'admin';
export interface Me {
  identity: { id: string; email: string; firstName: string; lastName: string; phone: string | null; language: string; locale: string; timezone: string; distanceUnit: string; totpEnabled: boolean };
  membership: { id: string; role: 'admin' | 'editor' | 'standard'; isOwner: boolean; capabilities: string[]; startPage: string; notificationPrefs: Record<string, string[]> };
  tenant: { id: string; name: string; country?: string | null; status: string; trialEndsAt: string | null; demoMode: boolean; orgCode: string; checklistDismissed: boolean; limits: Record<string, number>; sandbox: boolean; rootGroupId: string };
  group: { id: string; name: string; path: string; currency: string };
  permissions: Record<string, Level>;
  flags: string[];
  memberships: Array<{ id: string; tenantId: string; role: string; tenantName: string }>;
  impersonatedBy: string | null;
  config: { brand: string; qrUrl: string; mcpUrl: string; teleHost: string; telePort: number; env: string };
}

const RANK: Record<Level, number> = { none: 0, view: 1, create_own: 2, edit: 3, admin: 4 };
const Ctx = createContext<Me | null>(null);

export function useMeQuery() {
  return useQuery({ queryKey: ['me'], queryFn: () => get<Me>('/v1/me'), staleTime: 60_000, retry: false });
}

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  return <Ctx.Provider value={me}>{children}</Ctx.Provider>;
}

export function useMe(): Me {
  const m = useContext(Ctx);
  if (!m) throw new Error('useMe outside session');
  return m;
}

export function useCan() {
  const me = useMe();
  return (module: string, level: Level = 'view') => RANK[(me.permissions[module] ?? 'none') as Level] >= RANK[level];
}

export function useFlag() {
  const me = useMe();
  return (flag: string) => me.flags.includes(flag);
}
